import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CreateUpdateKioskModal, DeleteKioskModal, KioskLabelsModal } from "modules/KioskDashboard/components";
import { useDisableKioskMutation, useEnableKioskMutation, useKiosksQuery } from "modules/KioskDashboard/components/KioskDashboardTable/hooks";
import "modules/KioskDashboard/components/KioskDashboardTable/KioskDashboardTable.scss";

import type { Kiosk } from "models";

const columnHelper = createColumnHelper<Kiosk>();

export const KioskDashboardTable = () => {
  const { t } = useTranslation();

  const [kioskToDeleteId, setKioskToDeleteId] = useState(0);
  const [kioskToUpdate, setKioskToUpdate] = useState<Kiosk | null>(null);
  const [kioskToDisplayLabels, setKioskToDisplayLabels] = useState<Kiosk | null>(null);

  const canUpdateKiosk = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Kiosk);
  const canDeleteKiosk = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.Kiosk);

  const { mutate: enableKiosk } = useEnableKioskMutation();
  const { mutate: disableKiosk } = useDisableKioskMutation();
  const { data: kiosks, isFetching: isKiosksFetching } = useKiosksQuery();

  const columns = [
    columnHelper.accessor((row, index) => ({ row, index }), {
      id: "index",
      header: () => <span className="index-column-header"> # </span>,
      size: 30,
      cell: ({
        row: {
          index,
          original: { active }
        }
      }) => (
        <div className={`index-column-container ${active ? "active-kiosk-container" : "inactive-kiosk-container"}`}>
          <span className={`${active ? "active-kiosk-text" : "inactive-kiosk-text"}`}>{index + 1}</span>
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "name",
      header: () => <span>{t("v8_name", "Name")}</span>,
      size: 750,
      cell: ({
        row: {
          original: { name }
        }
      }) => <span>{name}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "front_end_version",
      header: () => <span>{t("v8_version", "Version")}</span>,
      size: 30,
      cell: ({
        row: {
          original: { front_end_version }
        }
      }) => <span>{front_end_version || null}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "actions",
      header: "",
      size: 900,
      cell: ({ row: { original: kiosk } }) => (
        <div className="actions-container">
          <div className="action-button-container" onClick={() => canUpdateKiosk && (kiosk.active ? disableKiosk(kiosk.id) : enableKiosk(kiosk.id))}>
            <Icon className={`${kiosk.active ? "circle pause" : "play"} action-button-icon-center`} color={kiosk.active ? "red" : "green"} disabled={!canUpdateKiosk} />
          </div>
          <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.KioskLabels}>
            <div className="action-button-container" onClick={() => setKioskToDisplayLabels(kiosk)}>
              <Icon className="list action-button-icon-center" color="purple" />
            </div>
          </Can>
          <div className="action-edit-button-container" onClick={() => canUpdateKiosk && setKioskToUpdate(kiosk)}>
            <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" disabled={!canUpdateKiosk} />
          </div>
          <div className="action-button-container" onClick={() => canDeleteKiosk && setKioskToDeleteId(kiosk.id)}>
            <Icon className="trash action-button-icon-center" color="red" disabled={!canDeleteKiosk} />
          </div>
        </div>
      )
    })
  ];

  return (
    <div className="KioskDashboardTable">
      <ReactTable isFetching={isKiosksFetching} columns={columns} data={kiosks} />

      <DeleteKioskModal kioskId={kioskToDeleteId} resetDeleteKioskId={() => setKioskToDeleteId(0)} />
      <CreateUpdateKioskModal isOpen={Boolean(kioskToUpdate)} onClose={() => setKioskToUpdate(null)} kioskToUpdate={kioskToUpdate} />
      <KioskLabelsModal kiosk={kioskToDisplayLabels} handleClose={() => setKioskToDisplayLabels(null)} />
    </div>
  );
};
